/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { ExchangeMessage, ThreadCluster, UserAuth } from '@repo/common';
import { get } from 'svelte/store';
import { getHandler } from '../sharedlib';
import { userAuthToken } from '../stores/auth.store';
import { getPrivateClient } from './common.data';

import { debounceAsync } from '@repo/toolslib/src/utils/debounce.util';
import { isAxiosError } from 'axios';

import constants from '../utils/constants';

import type { SyncItemsInput } from '@repo/common/types/AdapterSync';
import { upsertItemDataToCloud } from '../utils/cloud-sync.util';

export const getThreads = async (
  $userAuthToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<ThreadCluster[]> => {
  if (!$userAuthToken) {
    console.debug('Skipping getThreads because user is not logged in.');
    return [];
  }

  const client = getPrivateClient($userAuthToken, { timeout: constants.TIMEOUT_XL, retries: 3 });
  try {
    const response = await client.get(`/threads`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 404) {
        // Expected behavior if no threads are found
        return [];
      }
      if (error.response?.status === 400) {
        // Handle 400 error (no email folders enabled) by returning empty list
        console.debug('No threads found, returning empty list');
        return [];
      }
      console.debug('Error getting threads', error.response?.data);
    } else {
      console.debug('Error getting threads', error);
    }
    return [];
  }
};

export const reindexThreads = async (
  $userAuthToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  limit: number = 50
): Promise<void> => {
  const client = getPrivateClient($userAuthToken, { timeout: constants.TIMEOUT_XL, retries: 3 });
  const response = await client.post(`/threads`, {
    data: {
      limit
    }
  });
  return response.data;
};

/*
  Uses the getHandler().syncItems to upsert the conversation and messages to the cloud.
*/
export const upsertItemsToCloud = async (items: SyncItemsInput) => {
  const $userAuthToken = get(userAuthToken);
  if (!$userAuthToken) {
    throw new Error('User not authenticated');
  }

  // Use the handler's syncItems method to sync both conversation and messages
  await getHandler().syncItems(items, $userAuthToken);
};

export const upsertTranscriptToCloud = async (
  transcriptId: string,
  messages: ExchangeMessage[]
) => {
  const $userAuthToken = get(userAuthToken);
  if (!$userAuthToken) {
    throw new Error('User not authenticated');
  }

  // Create sync items input with transcript and message IDs
  const syncItems: SyncItemsInput = {
    transcript: [{ id: transcriptId }],
    message: messages.map((msg) => ({ id: msg.id }))
  };

  // Use the handler's syncItems method to sync both transcript and messages
  await getHandler().syncItems(syncItems, $userAuthToken);
};

export const deleteAllMemories = async (
  _$userAuthToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<{ count: number; deletedFilesCount: number }> => {
  throw new Error('Not implemented');
};

export const deleteMemory = async (
  _memoryId: string,
  _$userAuthToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<void> => {
  throw new Error('Not implemented');
};

export const debouncedUpsertTranscriptToCloud = debounceAsync(
  upsertTranscriptToCloud,
  'upsertTranscriptToCloud',
  5000
);
export const debouncedUpsertItemsToCloud = debounceAsync(
  upsertItemsToCloud,
  'upsertItemsToCloud',
  5000
);

// Re-export from utility for backwards compatibility
export { upsertItemDataToCloud } from '../utils/cloud-sync.util';

export const debouncedUpsertItemDataToCloud = debounceAsync(
  upsertItemDataToCloud,
  'upsertItemDataToCloud',
  5000
);
